import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Download } from "lucide-react";

const exportCsv = async () => {
  try {
    const { data } = await api.get("/admin/sol/claims.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `wolfpack-claims-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

function Stat({ label, children, testid }) {
  return <div><p className="text-xs text-slate-400">{label}</p><div className="font-display text-2xl text-white" data-testid={testid}>{children}</div></div>;
}

function Claims({ claims }) {
  if (!claims.length) return <p className="text-xs text-slate-500" data-testid="admin-sol-no-claims">No claims yet.</p>;
  return (
    <div className="space-y-2" data-testid="admin-sol-claims">
      {claims.map((c) => (
        <div key={c.id} className="glass rounded-xl p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid={`admin-sol-claim-${c.id}`}>
          <span className="text-white font-semibold">#{c.no} · {c.name}</span>
          <span className={c.status === "complete" ? "text-emerald-300" : c.status === "failed" ? "text-red-300" : "text-amber-200"}>{c.status}</span>
          <span className="text-slate-400">{c.buyer_email}</span>
          <span className="font-mono-game text-slate-300 truncate max-w-[16rem]">{c.wallet}</span>
          {c.claimed_at && <span className="text-slate-500">{new Date(c.claimed_at).toLocaleString()}</span>}
          {c.status === "complete" && <span className="text-amber-200" data-testid={`admin-sol-claim-fee-${c.id}`}>{c.paid_by === "player" ? `+${c.fee_sol ?? 0} SOL fee` : "site paid"}</span>}
          {c.explorer && <a href={c.explorer} target="_blank" rel="noreferrer" data-testid={`admin-sol-claim-nft-${c.id}`} className="text-cyan-300 inline-flex items-center gap-1 hover:underline">View NFT <ExternalLink className="w-3 h-3" /></a>}
          {c.tx_explorer && <a href={c.tx_explorer} target="_blank" rel="noreferrer" data-testid={`admin-sol-claim-tx-${c.id}`} className="text-cyan-300 inline-flex items-center gap-1 hover:underline">Payment <ExternalLink className="w-3 h-3" /></a>}
          {c.error && <span className="text-red-300 w-full">{c.error}</span>}
          {c.reject_reason && c.status !== "complete" && <span className="text-slate-500 w-full" data-testid={`admin-sol-claim-reason-${c.id}`}>Last rejected: {c.reject_reason}</span>}
          {c.status === "complete" && c.sign_mode === "site_first" && <span className="text-slate-500" data-testid={`admin-sol-claim-mode-${c.id}`}>needed 2nd approval</span>}
        </div>
      ))}
    </div>
  );
}

function MintFee({ fee }) {
  return <p className="text-sm text-slate-300" data-testid="admin-sol-mint-fee">Mint fee: <span className="font-mono-game text-amber-200">{fee} SOL</span> (fixed)</p>;
}

export default function AdminSolana() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/sol").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-slate-500">Loading…</p>;
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const devnet = d.network === "devnet";
  const switchNet = () => {
    const to = devnet ? "mainnet" : "devnet";
    if (to === "mainnet" && !window.confirm("Switch to the live Solana network? Real SOL will be spent from the minting wallet for every claim.")) return;
    run(() => api.post("/admin/sol/config", { network: to }), `Now on the ${to === "mainnet" ? "live" : "test"} network. Turn claiming on when ready.`);
  };
  return (
    <div className="space-y-6" data-testid="admin-solana">
      <div className="glass rounded-2xl p-6 flex flex-wrap items-center gap-8">
        <div className="flex items-center gap-3">
          <Switch data-testid="admin-sol-enabled" checked={d.enabled} disabled={busy} onCheckedChange={(v) => run(() => api.post("/admin/sol/config", { enabled: v }), v ? "Claiming is open" : "Claiming is paused")} />
          <span className="text-sm text-white">{d.enabled ? "Claiming is ON" : "Claiming is OFF"}</span>
        </div>
        <Stat label="Network" testid="admin-sol-network">
          <span className={devnet ? "text-cyan-200" : "text-emerald-300"}>{devnet ? "Test network" : "Live"}</span>
        </Stat>
        <Stat label="Minting wallet balance" testid="admin-sol-balance">{d.balance == null ? "—" : `${d.balance.toFixed(4)} SOL`}</Stat>
        <Stat label="Claimed" testid="admin-sol-claimed">{d.claimed}</Stat>
        <Stat label={`Mint fees earned (${d.mint_fee} SOL each)`} testid="admin-sol-fees">{d.fees_earned} SOL</Stat>
        <button data-testid="admin-sol-switch-network" disabled={busy || (devnet && !d.has_live_key)} onClick={switchNet}
          className="ml-auto px-4 py-2 rounded-full text-xs border border-slate-600 hover:border-amber-300 disabled:opacity-40">
          {devnet ? "SWITCH TO LIVE" : "SWITCH TO TEST NETWORK"}
        </button>
      </div>
      <MintFee fee={d.mint_fee} />
      <p className="text-xs text-slate-500" data-testid="admin-sol-fee-note">Players pay for their own claims: the Solana network cost plus a {d.mint_fee} SOL mint fee that goes to the minting wallet.</p>
      {devnet && !d.has_live_key && <p className="text-xs text-slate-500" data-testid="admin-sol-live-note">To go live, ask your builder to add your own Solana minting wallet through the secure key entry. Until then everything runs on the free test network, where NFTs have no value.</p>}
      <div className="glass rounded-2xl p-6 space-y-4 text-sm">
        <div>
          <p className="text-xs text-slate-400 mb-1">Minting wallet ({devnet ? "test network" : "live"})</p>
          {d.mint_wallet ? (
            <div className="flex flex-wrap items-center gap-2">
              <code className="font-mono-game text-amber-100 break-all" data-testid="admin-sol-mint-wallet">{d.mint_wallet}</code>
              <button data-testid="admin-sol-copy-wallet" onClick={() => { navigator.clipboard.writeText(d.mint_wallet); toast.success("Address copied"); }} className="p-1.5 text-slate-400 hover:text-white"><Copy className="w-4 h-4" /></button>
              {devnet && <>
                <button data-testid="admin-sol-airdrop" disabled={busy} onClick={() => run(() => api.post("/admin/sol/airdrop"), "1 test SOL is on its way")} className="px-3 py-1.5 rounded-full text-xs border border-cyan-400/50 text-cyan-200 disabled:opacity-40">GET FREE TEST SOL</button>
                <a href="https://faucet.solana.com" target="_blank" rel="noreferrer" data-testid="admin-sol-faucet" className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">or use faucet.solana.com <ExternalLink className="w-3 h-3" /></a>
              </>}
            </div>
          ) : <p className="text-red-300">Not set up</p>}
        </div>
        <div>
          <p className="text-xs text-slate-400 mb-1">Collection: {d.collection_name} · {d.royalty_percent}% royalties to the minting wallet</p>
          {d.collection ? (
            <a href={d.collection_explorer} target="_blank" rel="noreferrer" data-testid="admin-sol-collection" className="font-mono-game text-cyan-300 hover:underline break-all inline-flex items-center gap-1">{d.collection} <ExternalLink className="w-3 h-3" /></a>
          ) : (
            <button data-testid="admin-sol-create-collection" disabled={busy || !d.mint_wallet} onClick={() => run(() => api.post("/admin/sol/collection"), "Collection created")} className="ember-btn px-4 py-2 rounded-full text-xs disabled:opacity-40">
              {busy ? "CREATING…" : "CREATE THE COLLECTION"}
            </button>
          )}
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <p className="text-sm text-amber-300 font-semibold">Claim history</p>
          <button data-testid="admin-sol-export" onClick={exportCsv} className="order-last sm:order-none px-3 py-1.5 rounded-full border border-amber-400/60 text-amber-200 text-xs hover:bg-amber-500/10 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />Download spreadsheet</button>
          <p className="text-xs text-slate-400" data-testid="admin-sol-claim-summary">{d.claims.length} claims · {d.claimed} minted · {d.claims.filter((c) => c.paid_by === "player").length} player-paid · {d.fees_earned} SOL in fees</p>
        </div>
        <Claims claims={d.claims} />
      </div>
    </div>
  );
}
